import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Save, Sparkles, ChevronRight, ChevronLeft, ChevronDown, ChevronUp, Calendar, ListChecks, FileSpreadsheet, RotateCcw, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { dailyApi } from "@/lib/setupApi";
import { bumpDailyEntriesVersion } from "@/lib/dailyEntriesBus";
import {
  buildDayBreakdown,
  daysOfMonth,
  ingredientsForDailyEntry,
  DAY_OF_WEEK_MEAL,
  MONTH_LIST,
} from "@/lib/mealPlan";
import { money, qty as qtyFmt, toNumber, ingredientAmount } from "@/lib/poshanConstants";
import { NO_MDM_REASONS } from "@/lib/dayKind";
import { buildDailyPayload, saveErrorMessage } from "@/lib/dailyPayload";

const DOW_LABEL = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function dowOf(dateStr) {
  return new Date(`${dateStr}T00:00:00`).getDay();
}

function emptyAttendance() {
  return { balVatika: 0, primary: 0, upperPrimary: 0, highSchool: 0 };
}

export default function BulkEntry({
  schoolId,
  active,
  year,
  month,
  existingEntries,
  blockedNew,
  blockedEdit,
  onSaved,
}) {
  const monthDays = useMemo(() => daysOfMonth(year, month - 1), [year, month]);
  const [step, setStep] = useState(1); // 1=select-days, 2=attendance, 3=preview
  const [selected, setSelected] = useState({}); // { date: true }
  const [overwrite, setOverwrite] = useState(false); // include dates that already have entries
  const [entryType, setEntryType] = useState("mdm"); // mdm | dryration
  // iter154 — Bulk-mark day kind. "working" (default) runs the regular
  // attendance-driven flow. "working_no_mdm" / "holiday" skip step 2
  // entirely and save a zero-attendance marker for every selected date.
  const [bulkDayKind, setBulkDayKind] = useState("working");
  const [bulkNoMdmReason, setBulkNoMdmReason] = useState("");
  const [bulkNoMdmReasonOther, setBulkNoMdmReasonOther] = useState("");
  const [rows, setRows] = useState([]); // [{ date, dow, mealName, attendance, _ingredients }]
  const [expanded, setExpanded] = useState({});
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0, errors: [] });

  // Build default selection on month change: working days (Mon–Sat), exclude existing entries unless overwrite.
  // iter154 — When the user has picked No-MDM / Holiday bulk-mark, start with
  // an EMPTY selection so they must deliberately tap each date (prevents
  // accidental month-wide marking of 20+ days).
  useEffect(() => {
    const init = {};
    if (bulkDayKind === "working") {
      monthDays.forEach((d) => {
        const dow = dowOf(d);
        const hasExisting = existingEntries.some((e) => e.date === d);
        if (dow !== 0 && (!hasExisting || overwrite)) init[d] = true;
      });
    }
    setSelected(init);
    setStep(1);
    setRows([]);
    setExpanded({});
    setProgress({ done: 0, total: 0, errors: [] });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, month, existingEntries.length, bulkDayKind]);

  const toggleDate = (d) => {
    setSelected((s) => ({ ...s, [d]: !s[d] }));
  };

  const selectAllWorking = () => {
    const next = {};
    monthDays.forEach((d) => {
      const dow = dowOf(d);
      const hasExisting = existingEntries.some((e) => e.date === d);
      if (dow !== 0 && (!hasExisting || overwrite)) next[d] = true;
    });
    setSelected(next);
  };

  const clearAll = () => setSelected({});

  const selectedDates = useMemo(
    () => monthDays.filter((d) => selected[d]),
    [monthDays, selected],
  );

  // Step 1 → Step 2: build initial rows from selectedDates, prefill from existing entry if any
  const goToAttendance = () => {
    if (blockedNew) {
      toast.error("Month is soft/hard locked — cannot create new entries");
      return;
    }
    if (selectedDates.length === 0) {
      toast.error("Pick at least one working day");
      return;
    }
    // iter154 — When bulk-marking as No-MDM or Holiday, skip the attendance
    // step entirely and save a zero-attendance marker for each date.
    if (bulkDayKind === "working_no_mdm") {
      if (!bulkNoMdmReason) {
        toast.error("Pick a No-MDM reason first");
        return;
      }
      if (bulkNoMdmReason === "other" && !bulkNoMdmReasonOther.trim()) {
        toast.error('Describe the "Other" reason');
        return;
      }
      // Confirmation guard — accidental month-wide marks are costly.
      if (selectedDates.length > 5) {
        const ok = typeof window !== "undefined" && window.confirm(
          `This will mark ${selectedDates.length} dates as No MDM (${bulkNoMdmReason}). Continue?`,
        );
        if (!ok) return;
      }
      void handleBulkMarkSave();
      return;
    }
    if (bulkDayKind === "holiday") {
      if (selectedDates.length > 5) {
        const ok = typeof window !== "undefined" && window.confirm(
          `This will mark ${selectedDates.length} dates as Holiday. Continue?`,
        );
        if (!ok) return;
      }
      void handleBulkMarkSave();
      return;
    }
    const initial = selectedDates.map((d) => {
      const existing = existingEntries.find((e) => e.date === d);
      const dow = dowOf(d);
      const meal = DAY_OF_WEEK_MEAL[dow];
      const att = existing?.attendance
        ? { ...emptyAttendance(), ...existing.attendance }
        : entryType === "dryration" && active
          ? {
              balVatika: toNumber(active.balVatikaEnrollment),
              primary: toNumber(active.primaryEnrollment),
              upperPrimary: toNumber(active.upperPrimaryEnrollment),
              highSchool: toNumber(active.highSchoolEnrollment),
            }
          : emptyAttendance();
      return {
        date: d,
        dow,
        mealName: meal.mealName,
        mealType: meal.mealType,
        attendance: att,
        existingId: existing?.id || null,
      };
    });
    setRows(initial);
    setStep(2);
  };

  const updateRowAtt = (date, key, value) => {
    setRows((rs) =>
      rs.map((r) =>
        r.date === date
          ? { ...r, attendance: { ...r.attendance, [key]: toNumber(value) } }
          : r,
      ),
    );
  };

  // Apply value to all rows for the given attendance key (BV/Primary/UP/HS)
  const applyToAllRows = (key, value) => {
    const num = toNumber(value);
    setRows((rs) => rs.map((r) => ({ ...r, attendance: { ...r.attendance, [key]: num } })));
    toast.success(`Applied to ${rows.length} day${rows.length === 1 ? "" : "s"}`);
  };

  // Apply enrolment to all rows (handy for Dry Ration)
  const applyEnrolmentToAll = () => {
    if (!active) return toast.error("No active school");
    setRows((rs) =>
      rs.map((r) => ({
        ...r,
        attendance: {
          balVatika: toNumber(active.balVatikaEnrollment),
          primary: toNumber(active.primaryEnrollment),
          upperPrimary: toNumber(active.upperPrimaryEnrollment),
          highSchool: toNumber(active.highSchoolEnrollment),
        },
      })),
    );
    toast.success("Filled all rows with school enrolment");
  };

  // Step 2 → Step 3: generate ingredient breakdown for each row
  const generatePreview = () => {
    const emptyRows = rows.filter((r) => {
      const a = r.attendance;
      return (
        toNumber(a.balVatika) +
          toNumber(a.primary) +
          toNumber(a.upperPrimary) +
          toNumber(a.highSchool) ===
        0
      );
    });
    if (emptyRows.length === rows.length) {
      toast.error("Enter attendance in at least one row");
      return;
    }

    const next = rows.map((r) => {
      const breakdown = buildDayBreakdown(r.date, r.attendance);
      const ingredients = ingredientsForDailyEntry(r.date, r.attendance);
      return { ...r, _breakdown: breakdown, _ingredients: ingredients };
    });
    setRows(next);
    setStep(3);
    toast.success(`Generated ingredients for ${next.length} day${next.length === 1 ? "" : "s"}`);
  };

  const toggleExpanded = (d) =>
    setExpanded((e) => ({ ...e, [d]: !e[d] }));

  // Save all rows
  const handleSaveAll = async () => {
    if (!schoolId) return toast.error("Pick an active school first");
    if (blockedEdit) return toast.error("Month is hard-locked");

    const toSave = rows.filter((r) => {
      const a = r.attendance;
      const total =
        toNumber(a.balVatika) +
        toNumber(a.primary) +
        toNumber(a.upperPrimary) +
        toNumber(a.highSchool);
      return total > 0;
    });

    if (toSave.length === 0) {
      toast.error("Nothing to save");
      return;
    }

    setSaving(true);
    setProgress({ done: 0, total: toSave.length, errors: [] });
    const errors = [];
    let lastErr = "";
    for (let i = 0; i < toSave.length; i += 1) {
      const r = toSave[i];
      const cleaned = (r._ingredients || []).filter(
        (ing) =>
          (ing.name || "").trim() !== "" &&
          (toNumber(ing.quantity) > 0 || toNumber(ing.rate) > 0),
      );
      try {
        // eslint-disable-next-line no-await-in-loop
        await dailyApi.upsert(
          buildDailyPayload({ schoolId, date: r.date, dayKind: "working", entryType, attendance: r.attendance, ingredients: cleaned }),
          r.existingId || undefined,
        );
        setProgress((p) => ({ ...p, done: p.done + 1 }));
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Bulk save failed for", r.date, err?.response?.data, err);
        errors.push(r.date);
        lastErr = saveErrorMessage(err);
        setProgress((p) => ({ ...p, done: p.done + 1, errors: [...p.errors, r.date] }));
      }
    }
    setSaving(false);
    if (errors.length === 0) {
      toast.success(`Saved ${toSave.length} day${toSave.length === 1 ? "" : "s"}`);
    } else {
      toast.error(`Saved ${toSave.length - errors.length}/${toSave.length}, ${errors.length} failed — ${lastErr}`);
    }
    // iter147 — bump the shared daily-entries version so any open MDCF /
    // Reports / MDM Bill tabs auto-refresh with the new bulk data.
    if (toSave.length - errors.length > 0) bumpDailyEntriesVersion();
    if (onSaved) await onSaved();
    // Reset for next batch
    setStep(1);
    setRows([]);
    setExpanded({});
  };

  const restart = () => {
    setStep(1);
    setRows([]);
    setExpanded({});
    setProgress({ done: 0, total: 0, errors: [] });
  };

  // iter154 — Bulk-mark No-MDM / Holiday save path. Skips step 2 (no
  // attendance to collect) and writes a zero-attendance marker for each
  // selected date. Mirrors single-entry save shape so legacy
  // reports/MDCF read it identically.
  const handleBulkMarkSave = async () => {
    if (!schoolId) return toast.error("Pick an active school first");
    if (blockedEdit) return toast.error("Month is hard-locked");
    const dates = selectedDates;
    if (dates.length === 0) return toast.error("Pick at least one day");

    setSaving(true);
    setProgress({ done: 0, total: dates.length, errors: [] });
    const errors = [];
    let lastErr = "";
    for (let i = 0; i < dates.length; i += 1) {
      const d = dates[i];
      const existing = existingEntries.find((x) => x.date === d);
      const payload = buildDailyPayload({
        schoolId, date: d, dayKind: bulkDayKind, noMdmReason: bulkNoMdmReason,
        noMdmReasonOther: bulkNoMdmReason === "other" ? bulkNoMdmReasonOther.trim() : undefined,
      });
      try {
        // eslint-disable-next-line no-await-in-loop
        await dailyApi.upsert(payload, existing?.id || undefined);
        setProgress((p) => ({ ...p, done: p.done + 1 }));
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Bulk-mark save failed for", d, err?.response?.data, err);
        errors.push(d);
        lastErr = saveErrorMessage(err);
        setProgress((p) => ({ ...p, done: p.done + 1, errors: [...p.errors, d] }));
      }
    }
    setSaving(false);
    const labelKind = bulkDayKind === "holiday" ? "holidays" : "No-MDM days";
    if (errors.length === 0) {
      toast.success(`Marked ${dates.length} ${labelKind}`);
    } else {
      toast.error(`Marked ${dates.length - errors.length}/${dates.length} · ${errors.length} failed — ${lastErr}`);
    }
    if (dates.length - errors.length > 0) bumpDailyEntriesVersion();
    if (onSaved) await onSaved();
    // Reset for next batch
    setBulkDayKind("working");
    setBulkNoMdmReason("");
    setBulkNoMdmReasonOther("");
    setStep(1);
  };

  const totalAttendance = (a) =>
    toNumber(a.balVatika) +
    toNumber(a.primary) +
    toNumber(a.upperPrimary) +
    toNumber(a.highSchool);

  const grandTotalCost = rows.reduce((s, r) => {
    const lines = r._ingredients || [];
    return s + lines.reduce((ss, ing) => ss + ingredientAmount(ing), 0);
  }, 0);

  return (
    <section
      data-testid="bulk-entry-card"
      className="rounded-xl border border-border bg-white p-6 shadow-[0_1px_0_rgba(44,53,40,0.04)] sm:p-8"
    >
      {/* Stepper */}
      <div className="mb-6 flex flex-wrap items-center gap-2 text-xs font-semibold uppercase tracking-[0.16em] text-ink-700">
        {[
          { n: 1, label: "Pick Working Days", icon: Calendar },
          { n: 2, label: "Enter Attendance", icon: ListChecks },
          { n: 3, label: "Generate & Save", icon: FileSpreadsheet },
        ].map((s, i) => (
          <div key={s.n} className="flex items-center gap-2">
            <span
              data-testid={`bulk-step-${s.n}`}
              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 ${
                step === s.n
                  ? "bg-clay text-white"
                  : step > s.n
                    ? "bg-moss text-white"
                    : "bg-sand-100 text-ink-700"
              }`}
            >
              <s.icon className="h-3.5 w-3.5" /> {s.n}. {s.label}
            </span>
            {i < 2 && <ChevronRight className="h-3.5 w-3.5 text-ink-700/60" />}
          </div>
        ))}
      </div>

      {blockedNew && step === 1 && (
        <div
          className="mb-5 flex items-start gap-2 rounded-lg border border-ochre bg-ochre/10 px-4 py-3 text-sm text-ochre"
          data-testid="bulk-locked-banner"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>This month is locked — bulk entry is disabled. Unlock above to proceed.</span>
        </div>
      )}

      {/* Step 1: Pick days */}
      {step === 1 && (
        <div data-testid="bulk-step-1-content">
          {/* iter154 — Bulk Day Kind picker. Default "working" uses the
              existing attendance flow. Picking No MDM / Holiday converts
              the Continue button into a direct bulk-mark save so schools
              can mark a stretch of flood/cyclone days in one tap. */}
          <div
            className="mb-4 rounded-xl border border-clay/30 bg-clay/5 p-3"
            data-testid="bulk-daykind-picker"
            data-active-daykind={bulkDayKind}
          >
            <p className="text-[0.65rem] font-semibold uppercase tracking-[0.18em] text-clay-dark">
              Mark selected dates as
            </p>
            <div className="mt-2 inline-flex flex-wrap gap-1 rounded-full border border-border bg-white p-1">
              {[
                { v: "working",        label: "Working (enter attendance)",      activeBg: "bg-moss text-white" },
                { v: "working_no_mdm", label: "No MDM · ପରିବେଷଣ ହୋଇନାହିଁ",      activeBg: "bg-ochre text-white" },
                { v: "holiday",        label: "Holiday · ଛୁଟି",                   activeBg: "bg-clay text-white" },
              ].map((opt) => (
                <button
                  key={opt.v}
                  type="button"
                  data-testid={`bulk-daykind-${opt.v}`}
                  onClick={() => setBulkDayKind(opt.v)}
                  className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors duration-150 ${
                    bulkDayKind === opt.v ? opt.activeBg : "text-ink-700 hover:text-clay"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            {bulkDayKind === "working_no_mdm" && (
              <div className="mt-3 rounded-lg border-2 border-ochre/40 bg-ochre/10 p-3" data-testid="bulk-no-mdm-reason-block">
                <p className="mb-2 text-[0.68rem] font-semibold uppercase tracking-[0.14em] text-ochre-dark">
                  ❗ Reason · କାରଣ <span className="text-rose-600">*</span>
                </p>
                <div className="flex flex-wrap gap-2">
                  {NO_MDM_REASONS.map((r) => {
                    const active = bulkNoMdmReason === r.key;
                    return (
                      <button
                        key={r.key}
                        type="button"
                        data-testid={`bulk-no-mdm-reason-${r.key}`}
                        onClick={() => setBulkNoMdmReason(r.key)}
                        className={`min-h-[40px] rounded-lg border-2 px-3 text-xs font-semibold transition-all ${
                          active ? "border-ochre bg-ochre text-white shadow" : "border-slate-200 bg-white text-ink-800 hover:border-ochre/40"
                        }`}
                      >
                        {r.en}
                      </button>
                    );
                  })}
                </div>
                {bulkNoMdmReason === "other" && (
                  <input
                    type="text"
                    data-testid="bulk-no-mdm-reason-other-input"
                    value={bulkNoMdmReasonOther}
                    onChange={(e) => setBulkNoMdmReasonOther(e.target.value)}
                    placeholder="Describe the reason · କାରଣ ଲେଖନ୍ତୁ"
                    className="mt-2 h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm focus:border-ochre focus:outline-none"
                  />
                )}
                <p className="mt-2 rounded bg-ochre/15 px-2.5 py-1.5 text-[0.68rem] font-semibold text-ochre-dark">
                  Attendance will be saved as 0 and rice / cooking cost will not be deducted.
                </p>
              </div>
            )}
            {bulkDayKind === "holiday" && (
              <p className="mt-3 rounded-lg border border-clay/40 bg-clay/10 p-2.5 text-xs font-semibold text-clay-dark" data-testid="bulk-holiday-note">
                All selected dates will be saved as ଛୁଟି · Holiday with 0 attendance.
              </p>
            )}
          </div>

          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="font-heading text-xl font-semibold text-ink-900">
                {MONTH_LIST[month - 1].label} {year} — Pick Working Days
              </h3>
              <p className="mt-1 text-sm text-ink-700">
                Tick each date that the kitchen ran. Sundays & existing entries are unticked by default.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <label className="inline-flex items-center gap-2 text-xs font-semibold text-ink-700">
                <input
                  type="checkbox"
                  data-testid="bulk-overwrite-toggle"
                  checked={overwrite}
                  onChange={(e) => setOverwrite(e.target.checked)}
                />
                Include dates already entered (overwrite)
              </label>
              <Button
                size="sm"
                variant="outline"
                onClick={selectAllWorking}
                data-testid="bulk-select-all"
              >
                Select all working days
              </Button>
              <Button size="sm" variant="ghost" onClick={clearAll} data-testid="bulk-clear-all">
                Clear
              </Button>
            </div>
          </div>

          <div
            className="grid grid-cols-7 gap-2 rounded-xl border border-border bg-sand-50 p-3"
            data-testid="bulk-day-grid"
          >
            {DOW_LABEL.map((l) => (
              <div
                key={l}
                className="text-center text-[0.65rem] font-semibold uppercase tracking-[0.18em] text-ink-700/70"
              >
                {l}
              </div>
            ))}
            {/* Empty cells before the 1st */}
            {(() => {
              const firstDow = dowOf(monthDays[0]);
              return Array.from({ length: firstDow }).map((_, i) => <div key={`pad-${i}`} />);
            })()}
            {monthDays.map((d) => {
              const dow = dowOf(d);
              const hasExisting = existingEntries.some((e) => e.date === d);
              const isSel = !!selected[d];
              const dayNum = Number(d.slice(8, 10));
              const meal = DAY_OF_WEEK_MEAL[dow];
              const disabled = blockedNew && !hasExisting; // soft-lock: cannot add new
              return (
                <button
                  key={d}
                  type="button"
                  onClick={() => !disabled && toggleDate(d)}
                  disabled={disabled}
                  data-testid={`bulk-day-${d}`}
                  className={`group relative flex flex-col items-center gap-0.5 rounded-md border px-1.5 py-2 text-[0.7rem] font-medium transition-all duration-150 ${
                    isSel
                      ? "border-moss-dark bg-moss text-white shadow-sm"
                      : dow === 0
                        ? "border-border bg-sand-100 text-ink-700/60"
                        : hasExisting
                          ? "border-moss bg-moss/10 text-moss-dark"
                          : "border-border bg-white text-ink-900 hover:border-moss"
                  } ${disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"}`}
                >
                  <span className="text-base font-semibold leading-none">{dayNum}</span>
                  <span className="text-[0.6rem] uppercase tracking-wider opacity-80 leading-tight">
                    {meal.mealType === "manual" ? "Holiday" : meal.mealName.replace(" + Ladoo", "+L").replace(" Day", "")}
                  </span>
                  {hasExisting && (
                    <span className="absolute right-0.5 top-0.5 rounded-full bg-moss/80 px-1 text-[0.55rem] font-bold text-white">
                      ✓
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-ink-700" data-testid="bulk-selected-count">
              <b className="text-ink-900">{selectedDates.length}</b> day
              {selectedDates.length === 1 ? "" : "s"} selected
            </p>
            <Button
              data-testid="bulk-next-1"
              onClick={goToAttendance}
              disabled={selectedDates.length === 0 || blockedNew || saving}
              className="gap-2 bg-clay text-white hover:bg-clay-dark"
            >
              {bulkDayKind === "working_no_mdm"
                ? (saving ? `Marking… ${progress.done}/${progress.total}` : "Mark as No MDM")
                : bulkDayKind === "holiday"
                  ? (saving ? `Marking… ${progress.done}/${progress.total}` : "Mark as Holiday")
                  : "Continue"}
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Step 2: Attendance per row */}
      {step === 2 && (
        <div data-testid="bulk-step-2-content">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="font-heading text-xl font-semibold text-ink-900">
                Enter Attendance ({rows.length} day{rows.length === 1 ? "" : "s"})
              </h3>
              <p className="mt-1 text-sm text-ink-700">
                Fill class-wise students for each day. Use <b>Apply to all</b> in any column header to mass-fill.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[0.78rem] font-semibold uppercase tracking-[0.16em] text-ink-700">
                Mode
              </span>
              <div className="inline-flex rounded-full border border-border bg-white p-1">
                {[
                  { v: "mdm", label: "MDM" },
                  { v: "dryration", label: "Dry Ration" },
                ].map((opt) => (
                  <button
                    key={opt.v}
                    type="button"
                    onClick={() => setEntryType(opt.v)}
                    data-testid={`bulk-entry-type-${opt.v}`}
                    className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors duration-150 ${
                      entryType === opt.v ? "bg-clay text-white" : "text-ink-700 hover:text-clay"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              {entryType === "dryration" && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={applyEnrolmentToAll}
                  data-testid="bulk-apply-enrolment"
                  className="gap-1.5"
                >
                  <Sparkles className="h-3.5 w-3.5" /> Fill enrolment
                </Button>
              )}
            </div>
          </div>

          <div className="overflow-x-auto rounded-xl border border-border" data-testid="bulk-attendance-table">
            <table className="w-full min-w-[860px] border-collapse text-sm">
              <thead className="bg-sand-100 text-left text-ink-700">
                <tr>
                  <th className="p-3 text-xs font-semibold uppercase tracking-[0.16em]">Date</th>
                  <th className="p-3 text-xs font-semibold uppercase tracking-[0.16em]">Day</th>
                  <th className="p-3 text-xs font-semibold uppercase tracking-[0.16em]">Menu</th>
                  {[
                    { k: "balVatika", l: "Bal Vatika" },
                    { k: "primary", l: "Primary" },
                    { k: "upperPrimary", l: "Upper Primary" },
                    { k: "highSchool", l: "High School" },
                  ].map((c) => (
                    <th key={c.k} className="p-2 text-xs font-semibold uppercase tracking-[0.16em]">
                      <div className="flex flex-col gap-1">
                        <span>{c.l}</span>
                        <input
                          type="number"
                          placeholder="Apply to all"
                          data-testid={`bulk-apply-all-${c.k}`}
                          className="h-7 w-full rounded border border-border bg-white px-2 text-[0.7rem] font-normal normal-case tracking-normal text-ink-900 placeholder:text-ink-700/60 focus:border-clay focus:outline-none"
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              applyToAllRows(c.k, e.currentTarget.value);
                              e.currentTarget.value = "";
                            }
                          }}
                          onBlur={(e) => {
                            if (e.currentTarget.value !== "") {
                              applyToAllRows(c.k, e.currentTarget.value);
                              e.currentTarget.value = "";
                            }
                          }}
                        />
                      </div>
                    </th>
                  ))}
                  <th className="p-3 text-xs font-semibold uppercase tracking-[0.16em]">Total</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const isManual = DAY_OF_WEEK_MEAL[r.dow].mealType === "manual";
                  return (
                    <tr
                      key={r.date}
                      data-testid={`bulk-row-${r.date}`}
                      className={`border-t border-border ${isManual ? "bg-sand-100/40" : ""}`}
                    >
                      <td className="p-2.5 font-mono text-ink-900">{r.date}</td>
                      <td className="p-2.5 text-ink-700">{DOW_LABEL[r.dow]}</td>
                      <td className="p-2.5">
                        <span
                          className={`rounded-full px-2 py-0.5 text-[0.7rem] font-semibold ${
                            isManual
                              ? "bg-sand-100 text-ink-700/70"
                              : r.mealType === "egg"
                                ? "bg-clay/15 text-clay"
                                : "bg-moss/15 text-moss-dark"
                          }`}
                        >
                          {r.mealName}
                        </span>
                      </td>
                      {["balVatika", "primary", "upperPrimary", "highSchool"].map((k) => (
                        <td key={k} className="p-1.5">
                          <input
                            type="number"
                            min="0"
                            value={r.attendance[k]}
                            data-testid={`bulk-att-${r.date}-${k}`}
                            onChange={(e) => updateRowAtt(r.date, k, e.target.value)}
                            className="h-9 w-24 rounded border border-border bg-white px-2 text-sm font-mono text-ink-900 focus:border-clay focus:outline-none"
                          />
                        </td>
                      ))}
                      <td className="p-2.5 text-right font-mono font-semibold text-clay">
                        {qtyFmt(totalAttendance(r.attendance))}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
            <Button
              variant="outline"
              data-testid="bulk-back-1"
              onClick={() => setStep(1)}
              className="gap-2"
            >
              <ChevronLeft className="h-4 w-4" /> Back
            </Button>
            <Button
              data-testid="bulk-generate-btn"
              onClick={generatePreview}
              className="gap-2 bg-clay text-white hover:bg-clay-dark"
            >
              <Sparkles className="h-4 w-4" /> Generate Ingredient Tables
            </Button>
          </div>
        </div>
      )}

      {/* Step 3: Preview & Save */}
      {step === 3 && (
        <div data-testid="bulk-step-3-content">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="font-heading text-xl font-semibold text-ink-900">
                Preview &amp; Save
              </h3>
              <p className="mt-1 text-sm text-ink-700">
                Auto-generated ingredients per day. Click any row to expand its ingredient table.
              </p>
            </div>
            <div className="rounded-lg bg-sand-100 px-4 py-2 text-sm text-ink-700">
              Grand Total Cooking Cost:{" "}
              <b className="text-clay" data-testid="bulk-grand-total">
                {money(grandTotalCost)}
              </b>
            </div>
          </div>

          <div className="space-y-2" data-testid="bulk-preview-list">
            {rows.map((r) => {
              const lines = r._ingredients || [];
              const dayCost = lines.reduce((s, ing) => s + ingredientAmount(ing), 0);
              const isOpen = expanded[r.date];
              const isManual = DAY_OF_WEEK_MEAL[r.dow].mealType === "manual";
              const tot = totalAttendance(r.attendance);
              return (
                <div
                  key={r.date}
                  data-testid={`bulk-preview-${r.date}`}
                  className={`overflow-hidden rounded-lg border ${
                    tot === 0 ? "border-dashed border-ink-700/30 bg-sand-50" : "border-border bg-white"
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => toggleExpanded(r.date)}
                    className="flex w-full items-center justify-between gap-3 px-4 py-2.5 text-left hover:bg-sand-50"
                  >
                    <div className="flex flex-wrap items-center gap-3">
                      <span className="font-mono font-semibold text-ink-900">{r.date}</span>
                      <span className="text-xs text-ink-700">{DOW_LABEL[r.dow]}</span>
                      <span
                        className={`rounded-full px-2 py-0.5 text-[0.65rem] font-semibold ${
                          isManual
                            ? "bg-sand-100 text-ink-700/70"
                            : r.mealType === "egg"
                              ? "bg-clay/15 text-clay"
                              : "bg-moss/15 text-moss-dark"
                        }`}
                      >
                        {r.mealName}
                      </span>
                      <span className="text-xs text-ink-700">
                        Att: <b className="text-ink-900 font-mono">{qtyFmt(tot)}</b>
                      </span>
                      <span className="text-xs text-ink-700">
                        Items: <b className="text-ink-900">{lines.length}</b>
                      </span>
                      <span className="text-sm font-semibold text-clay">{money(dayCost)}</span>
                      {tot === 0 && (
                        <span className="rounded-full bg-ochre/15 px-2 py-0.5 text-[0.65rem] font-semibold text-ochre">
                          Skipped (0 attendance)
                        </span>
                      )}
                    </div>
                    {isOpen ? <ChevronUp className="h-4 w-4 text-ink-700" /> : <ChevronDown className="h-4 w-4 text-ink-700" />}
                  </button>
                  {isOpen && lines.length > 0 && (
                    <div className="border-t border-border bg-sand-50/40">
                      <table className="w-full border-collapse text-xs">
                        <thead className="bg-sand-100 text-left text-ink-700">
                          <tr>
                            <th className="p-2 text-[0.65rem] font-semibold uppercase tracking-[0.16em]">Ingredient</th>
                            <th className="p-2 text-[0.65rem] font-semibold uppercase tracking-[0.16em]">Qty</th>
                            <th className="p-2 text-[0.65rem] font-semibold uppercase tracking-[0.16em]">Unit</th>
                            <th className="p-2 text-[0.65rem] font-semibold uppercase tracking-[0.16em]">Rate</th>
                            <th className="p-2 text-[0.65rem] font-semibold uppercase tracking-[0.16em]">Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {lines.map((ing, i) => (
                            <tr key={`${r.date}-${i}`} className="border-t border-border">
                              <td className="p-2 text-ink-900">{ing.name}</td>
                              <td className="p-2 font-mono text-ink-900">{qtyFmt(ing.quantity)}</td>
                              <td className="p-2 text-ink-700">{ing.unit}</td>
                              <td className="p-2 text-ink-700">{Number(ing.rate).toFixed(2)}</td>
                              <td className="p-2 font-semibold text-clay">{money(ingredientAmount(ing))}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {progress.total > 0 && (
            <div className="mt-4 rounded-lg bg-sand-100 px-4 py-3 text-sm text-ink-700" data-testid="bulk-progress">
              Saved {progress.done}/{progress.total}
              {progress.errors.length > 0 && (
                <span className="text-clay-dark"> · Failed: {progress.errors.join(", ")}</span>
              )}
            </div>
          )}

          <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" data-testid="bulk-back-2" onClick={() => setStep(2)} className="gap-2">
                <ChevronLeft className="h-4 w-4" /> Back to attendance
              </Button>
              <Button variant="ghost" data-testid="bulk-restart" onClick={restart} className="gap-2">
                <RotateCcw className="h-3.5 w-3.5" /> Restart
              </Button>
            </div>
            <Button
              data-testid="bulk-save-all-btn"
              onClick={handleSaveAll}
              disabled={saving || blockedEdit}
              className="gap-2 bg-clay text-white hover:bg-clay-dark"
            >
              <Save className="h-4 w-4" />
              {saving ? `Saving… ${progress.done}/${progress.total}` : `Save ${rows.filter((r) => totalAttendance(r.attendance) > 0).length} Entries`}
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}
